'use client';

import { useRef, type ComponentPropsWithoutRef, type JSX } from 'react';
import { useVideoAnalytics } from 'lib/hooks/useVideoAnalytics';
import {
  getVideoId,
  getVideoPlatform,
  withVideoPlayerApi,
} from 'lib/utils/videoUtils';

export type TrackedVideoIframeProps = ComponentPropsWithoutRef<'iframe'> & {
  /** Original content URL (watch / share link) used for platform + video ID */
  videoUrl: string;
  /** Display title included in analytics payloads */
  videoTitle: string;
};

/**
 * Iframe embed that enables YouTube/Vimeo player APIs and reports engagement
 * events through the site GTM analytics helpers.
 */
const VIDEO_IFRAME_ALLOW =
  'autoplay; fullscreen; picture-in-picture; encrypted-media; presentation';

const TrackedVideoIframe = ({
  videoUrl,
  videoTitle,
  src,
  sandbox,
  allow,
  ...iframeProps
}: TrackedVideoIframeProps): JSX.Element => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const platform = getVideoPlatform(videoUrl);
  const videoId = getVideoId(videoUrl);
  const embedSrc =
    typeof src === 'string' && src ? withVideoPlayerApi(src, platform) : src;

  useVideoAnalytics({
    iframeRef,
    platform,
    videoId,
    videoTitle,
    enabled: Boolean(embedSrc && videoId && platform),
  });

  // Vimeo's player API is blocked by sandbox; omitting it also avoids Chrome
  // keeping a sandboxed browsing context after the attribute is removed.
  const iframeSandbox = platform === 'vimeo' ? undefined : sandbox;
  const allowValue = allow?.includes('presentation')
    ? allow
    : [allow, VIDEO_IFRAME_ALLOW].filter(Boolean).join('; ');

  return (
    <iframe
      {...iframeProps}
      key={`video-${platform || 'unknown'}-${videoId || 'none'}-${iframeSandbox ? 'sandboxed' : 'open'}`}
      ref={iframeRef}
      src={embedSrc}
      allow={allowValue}
      {...(iframeSandbox != null ? { sandbox: iframeSandbox } : {})}
    />
  );
};

export default TrackedVideoIframe;
